import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Grid, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Box } from "@mui/material";
import { Cancel, Edit } from "@mui/icons-material";
import { CollapseCard, Button, TextField, DateTime, Dialog } from "@totalsoft/rocket-ui";
import BadgeIcon from "@mui/icons-material/Badge";
import CardNoResultsWrapper from "components/layout/CardNoResultsWrapper";
import { DenseHeaderCell } from "components/common/TableStyles";
import { useReactiveVar } from "@apollo/client";
import { synchronizationInProgressVar } from "variables";

export const EmployeesComponent = ({
  saving,
  hasWritePermission,
  employees,
  onOpenEditEmployee,
  onAddEmployee,
  onDeleteEmployee
}) => {
  const { t } = useTranslation();
  const synchronizationInProgress = useReactiveVar(synchronizationInProgressVar);
  const [employee, setEmployee] = useState({});
  const [openRemoveEmployeeConfirmation, setOpenRemoveEmployeeConfirmation] = useState(false);

  const disabled = useMemo(() => saving || !hasWritePermission, [saving, hasWritePermission]);

  const handleOpenRemoveConfirmation = useCallback(item => {
    setOpenRemoveEmployeeConfirmation(true);
    setEmployee(item);
  }, []);

  const handleCloseRemoveConfirmation = useCallback(() => {
    setOpenRemoveEmployeeConfirmation(false);
  }, []);

  const handleDeleteEmployee = useCallback(() => {
    setOpenRemoveEmployeeConfirmation(false);
    onDeleteEmployee(employee);
  }, [employee, onDeleteEmployee]);

  return (
    <>
      <CollapseCard
        id="employeesCard"
        icon={BadgeIcon}
        iconColor="primary"
        title={t("Partner.Employees")}
        defaultExpanded={synchronizationInProgress}
        toggleOnHeaderClick
      >
        <Grid item xs={12} sm={12} md={12}>
          <CardNoResultsWrapper content={employees}>
            <Table>
              <TableHead>
                <TableRow>
                  <DenseHeaderCell>{t("Employee.Name")}</DenseHeaderCell>
                  <DenseHeaderCell>{t("Employee.HireDate")}</DenseHeaderCell>
                  <DenseHeaderCell>{t("Employee.Address")}</DenseHeaderCell>
                  <DenseHeaderCell>{t("Employee.State")}</DenseHeaderCell>
                  <DenseHeaderCell></DenseHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees?.map((item, idx) => {
                  return (
                    <TableRow key={idx}>
                      <TableCell>
                        <TextField
                          id={"employee_" + idx + "_name"}
                          fullWidth
                          value={item.employeeName || ""}
                          disabled={true}
                        />
                      </TableCell>
                      <TableCell>
                        <DateTime
                          id={"employee_" + idx + "_hireDate"}
                          fullWidth
                          value={item.hireDate || ""}
                          disabled={true}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          id={"employee_" + idx + "_address"}
                          fullWidth
                          value={item.address || ""}
                          disabled={true}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          id={"employee_" + idx + "_employeeStateName"}
                          fullWidth
                          value={item.employeeStateName || ""}
                          disabled={true}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          id={"employee_" + idx + "_editEmployee"}
                          color="primary"
                          variant="text"
                          onClick={() => onOpenEditEmployee(item)}
                          tooltip={t("Tooltips.EditEmployee")}
                          disabled={disabled}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          id={"employee_" + idx + "_deleteEmployee"}
                          color="primary"
                          variant="text"
                          onClick={() => handleOpenRemoveConfirmation(item)}
                          tooltip={t("Tooltips.DeleteEmployee")}
                          disabled={disabled}
                        >
                          <Cancel />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardNoResultsWrapper>
          <Button
            id="employees_addBtn"
            variant="contained"
            sx={{ marginTop: "1.5rem" }}
            onClick={() => onAddEmployee()}
            disabled={disabled}
          >
            {t("Add")}
          </Button>
        </Grid>
      </CollapseCard>
      {openRemoveEmployeeConfirmation && (
        <Dialog
          aria-labelledby="remove-employee-title"
          open={openRemoveEmployeeConfirmation}
          showX={false}
          title={t("Employee.ConfirmationDialog.Title")}
          content={t("Employee.ConfirmationDialog.Description")}
          actions={
            <Box display="flex" gap={1} mb={1} mx={1}>
              <Button id={"employees_resetDataAcceptBtn"} onClick={handleDeleteEmployee}>
                {t("Employee.ConfirmationDialog.AcceptButton")}
              </Button>
              <Button id={"employees_resetDataRejectBtn"} onClick={handleCloseRemoveConfirmation}>
                {t("Employee.ConfirmationDialog.RejectButton")}
              </Button>
            </Box>
          }
        />
      )}
    </>
  );
};

EmployeesComponent.propTypes = {
  saving: PropTypes.bool.isRequired,
  hasWritePermission: PropTypes.bool.isRequired,
  employees: PropTypes.array.isRequired,
  onOpenEditEmployee: PropTypes.func.isRequired,
  onAddEmployee: PropTypes.func.isRequired,
  onDeleteEmployee: PropTypes.func.isRequired
};

export default EmployeesComponent;
